'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';

const PlatformActivityChart = dynamic(() => import('@/app/admin-panel/components/PlatformActivityChart'), { ssr: false });
const ContentBreakdownChart = dynamic(() => import('@/app/admin-panel/components/ContentBreakdownChart'), { ssr: false });

const kpiCards = [
{ id: 'kpi-movies', label: 'Total Movies', value: '2,847', change: '+23 this week', icon: 'FilmIcon', trend: 'up', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
{ id: 'kpi-pending', label: 'Pending Submissions', value: '7', change: '3 urgent', icon: 'InboxArrowDownIcon', trend: 'alert', color: 'text-gold', bg: 'bg-gold/10', border: 'border-gold/25' },
{ id: 'kpi-flagged', label: 'Flagged Reviews', value: '12', change: '4 new today', icon: 'FlagIcon', trend: 'alert', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/25' },
{ id: 'kpi-users', label: 'Active Users', value: '18,430', change: '+340 this week', icon: 'UserGroupIcon', trend: 'up', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
{ id: 'kpi-reviews', label: 'Reviews This Week', value: '1,240', change: '+18% vs last week', icon: 'StarIcon', trend: 'up', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
{ id: 'kpi-score', label: 'Platform Health', value: '94/100', change: 'Excellent', icon: 'HeartIcon', trend: 'up', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' }];


const submissions = [
{ id: 'sub-001', title: 'The Substance', year: 2024, genre: 'Horror', submittedBy: 'Marcus Kim', submitterRole: 'contributor', submittedDate: 'Jul 27, 2026', status: 'pending' as const, poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1d3af3d20-1769434207557.png", posterAlt: 'Dark neon aesthetic horror film', urgent: true },
{ id: 'sub-002', title: 'Longlegs', year: 2024, genre: 'Horror/Thriller', submittedBy: 'Elena Vasquez', submitterRole: 'contributor', submittedDate: 'Jul 26, 2026', status: 'pending' as const, poster: "https://img.rocket.new/generatedImages/rocket_gen_img_124770410-1780581387051.png", posterAlt: 'Dark gothic landscape for horror film', urgent: true },
{ id: 'sub-003', title: 'Alien: Romulus', year: 2024, genre: 'Sci-Fi/Horror', submittedBy: 'Kenji Watanabe', submitterRole: 'contributor', submittedDate: 'Jul 25, 2026', status: 'pending' as const, poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1ceaa7f1f-1773100391546.png", posterAlt: 'Dark cosmic space scene', urgent: false },
{ id: 'sub-004', title: 'Trap', year: 2024, genre: 'Thriller', submittedBy: 'Sofia Reyes', submitterRole: 'contributor', submittedDate: 'Jul 24, 2026', status: 'pending' as const, poster: "https://images.unsplash.com/photo-1715618117786-e96d7aedb5cb", posterAlt: 'Dark concert venue for thriller film', urgent: false },
{ id: 'sub-005', title: 'Twisters', year: 2024, genre: 'Action', submittedBy: 'James Okafor', submitterRole: 'contributor', submittedDate: 'Jul 23, 2026', status: 'approved' as const, poster: "https://images.unsplash.com/photo-1683669446787-3b4881d3d267", posterAlt: 'Dramatic stormy landscape', urgent: false },
{ id: 'sub-006', title: 'Borderlands', year: 2024, genre: 'Action/Comedy', submittedBy: 'Marcus Kim', submitterRole: 'contributor', submittedDate: 'Jul 22, 2026', status: 'rejected' as const, poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd207428-1772735248700.png", posterAlt: 'Colorful action film aesthetic', urgent: false },
{ id: 'sub-007', title: 'MaXXXine', year: 2024, genre: 'Horror', submittedBy: 'Priya Nair', submitterRole: 'contributor', submittedDate: 'Jul 21, 2026', status: 'pending' as const, poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1bef05f13-1772376525645.png", posterAlt: 'Classic cinema projector in dark setting', urgent: false }];


const flaggedReviews = [
{ id: 'flag-001', reviewId: 'rev-flag-001', movie: 'Saltburn', user: 'TrollingUser99', flagReason: 'Hate speech / offensive content', flagCount: 8, excerpt: 'This film is absolute garbage and anyone who likes it is...', date: 'Jul 28, 2026', severity: 'high' as const },
{ id: 'flag-002', reviewId: 'rev-flag-002', movie: 'Oppenheimer', user: 'HistoryDenier42', flagReason: 'Misinformation / factual inaccuracies', flagCount: 5, excerpt: 'Oppenheimer never actually built the bomb, this is all Hollywood propaganda...', date: 'Jul 27, 2026', severity: 'medium' as const },
{ id: 'flag-003', reviewId: 'rev-flag-003', movie: 'Poor Things', user: 'AnonymousUser', flagReason: 'Unmarked spoilers', flagCount: 12, excerpt: 'In the final scene, Bella chooses to...', date: 'Jul 27, 2026', severity: 'low' as const },
{ id: 'flag-004', reviewId: 'rev-flag-004', movie: 'Dune: Part Two', user: 'SpamAccount77', flagReason: 'Spam / promotional content', flagCount: 3, excerpt: 'Great movie! Also check out my website for free streaming at...', date: 'Jul 26, 2026', severity: 'high' as const },
{ id: 'flag-005', reviewId: 'rev-flag-005', movie: 'Past Lives', user: 'CasualViewer', flagReason: 'Off-topic / irrelevant', flagCount: 2, excerpt: "Didn\'t watch this but wanted to say I think Korean cinema is overrated in general...", date: 'Jul 25, 2026', severity: 'low' as const }];


const recentUsers = [
{ id: 'user-001', name: 'Sofia Reyes', email: 'sofia@cineworld.cloud', role: 'contributor' as const, status: 'active' as const, joined: 'Jan 12, 2025', reviews: 38, watchedCount: 247 },
{ id: 'user-002', name: 'James Okafor', email: 'james@cineworld.cloud', role: 'contributor' as const, status: 'active' as const, joined: 'Mar 5, 2025', reviews: 29, watchedCount: 189 },
{ id: 'user-003', name: 'Priya Nair', email: 'priya@cineworld.cloud', role: 'user' as const, status: 'active' as const, joined: 'May 22, 2025', reviews: 14, watchedCount: 134 },
{ id: 'user-004', name: 'Tomas Novak', email: 'tomas@cineworld.cloud', role: 'user' as const, status: 'active' as const, joined: 'Jun 3, 2025', reviews: 22, watchedCount: 312 },
{ id: 'user-005', name: 'TrollingUser99', email: 'troll99@temp.com', role: 'user' as const, status: 'suspended' as const, joined: 'Jul 1, 2026', reviews: 3, watchedCount: 12 },
{ id: 'user-006', name: 'Amara Diallo', email: 'amara@cineworld.cloud', role: 'contributor' as const, status: 'active' as const, joined: 'Feb 14, 2025', reviews: 31, watchedCount: 278 }];


export default function AdminDashboardContent() {
  const [selectedSubmissions, setSelectedSubmissions] = useState<string[]>([]);
  const [submissionFilter, setSubmissionFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [confirmModal, setConfirmModal] = useState<{type: string;id: string;title: string;} | null>(null);

  const toggleSubmissionSelect = (id: string) => {
    setSelectedSubmissions((prev) =>
    prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const filteredSubmissions = submissions.filter((s) => {
    if (submissionFilter === 'all') return true;
    return s.status === submissionFilter;
  });

  const filteredReviews = flaggedReviews.filter((r) => {
    if (reviewFilter === 'all') return true;
    return r.severity === reviewFilter;
  });

  return (
    <div className="min-h-screen">
      {/* Topbar */}
      <div className="h-16 border-b border-border bg-card flex items-center justify-between px-6 sticky top-0 z-10">
        <div>
          <h1 className="text-base font-700 text-foreground">Admin Dashboard</h1>
          <p className="text-xs text-muted-foreground">Last updated: 5 minutes ago</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="relative btn-ghost p-2 rounded-lg">
            <Icon name="BellIcon" size={18} className="text-muted-foreground" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
          </button>
          <div className="flex items-center gap-2 pl-2 border-l border-border">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-red-800 flex items-center justify-center text-xs font-800 text-white">
              A
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-600 text-foreground">Admin User</p>
              <p className="text-xs text-primary font-500">Super Admin</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-screen-2xl mx-auto">
        {/* KPI Grid — 6 cards → 3x2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mb-8">
          {kpiCards.map((card) =>
          <div
            key={card.id}
            className={`p-4 rounded-2xl bg-card border ${card.border} card-hover ${card.trend === 'alert' ? 'pulse-red' : ''}`}>
            
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-xl ${card.bg}`}>
                  <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={16} className={card.color} />
                </div>
                {card.trend === 'alert' &&
              <Icon name="ExclamationTriangleIcon" size={14} className="text-gold" />
              }
                {card.trend === 'up' &&
              <Icon name="ArrowTrendingUpIcon" size={14} className="text-green-400" />
              }
              </div>
              <div className={`text-2xl font-800 font-mono-nums ${card.color} mb-0.5`}>{card.value}</div>
              <div className="text-xs font-600 text-foreground">{card.label}</div>
              <div className={`text-xs mt-0.5 ${card.trend === 'alert' ? 'text-gold' : 'text-muted-foreground'}`}>{card.change}</div>
            </div>
          )}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div className="xl:col-span-2 p-5 rounded-2xl bg-card border border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-1 h-4 bg-primary rounded-full" />
                <h3 className="text-sm font-700 text-foreground">Platform Activity — Last 30 Days</h3>
              </div>
              <select className="input-dark text-xs px-2.5 py-1.5">
                <option>Last 30 days</option>
                <option>Last 90 days</option>
                <option>Last year</option>
              </select>
            </div>
            <PlatformActivityChart />
          </div>
          <div className="p-5 rounded-2xl bg-card border border-border">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1 h-4 bg-gold rounded-full" />
              <h3 className="text-sm font-700 text-foreground">Content Breakdown</h3>
            </div>
            <ContentBreakdownChart />
          </div>
        </div>

        {/* Submissions queue */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="w-1 h-5 bg-gold rounded-full" />
              <h2 className="text-base font-700 text-foreground">Contributor Submissions</h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-700 status-pending">7 Pending</span>
            </div>
            <div className="flex items-center gap-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((f) =>
              <button
                key={`sub-filter-${f}`}
                onClick={() => setSubmissionFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                submissionFilter === f ? 'bg-primary/15 text-primary border border-primary/25' : 'btn-ghost'}`
                }>
                
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              )}
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedSubmissions.length > 0 &&
          <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/10 border border-primary/25 mb-3 slide-up">
              <span className="text-sm font-600 text-foreground">{selectedSubmissions.length} selected</span>
              <div className="flex items-center gap-2 ml-2">
                <button className="btn-primary px-4 py-1.5 rounded-lg text-xs font-700 flex items-center gap-1.5">
                  <Icon name="CheckIcon" size={12} />
                  Approve All
                </button>
                <button className="px-4 py-1.5 rounded-lg text-xs font-600 bg-primary/15 text-primary border border-primary/25 flex items-center gap-1.5 hover:bg-primary/25 transition-colors">
                  <Icon name="XMarkIcon" size={12} />
                  Reject All
                </button>
              </div>
              <button onClick={() => setSelectedSubmissions([])} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Clear</button>
            </div>
          }

          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="rounded"
                      onChange={(e) => {
                        if (e.target.checked) setSelectedSubmissions(filteredSubmissions.filter((s) => s.status === 'pending').map((s) => s.id));else
                        setSelectedSubmissions([]);
                      }} />
                    
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Film</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden md:table-cell">Submitted By</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredSubmissions.map((sub) =>
                <tr key={sub.id} className={`thread-row group ${selectedSubmissions.includes(sub.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                      type="checkbox"
                      checked={selectedSubmissions.includes(sub.id)}
                      onChange={() => toggleSubmissionSelect(sub.id)}
                      className="rounded"
                      disabled={sub.status !== 'pending'} />
                    
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative w-8 h-11 rounded overflow-hidden flex-shrink-0 bg-muted">
                          <AppImage src={sub.poster} alt={sub.posterAlt} fill className="object-cover" sizes="32px" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-600 text-foreground">{sub.title}</span>
                            {sub.urgent && sub.status === 'pending' &&
                          <span className="px-1.5 py-0.5 rounded text-xs font-700 bg-gold/15 text-gold border border-gold/25">Urgent</span>
                          }
                          </div>
                          <span className="text-xs text-muted-foreground">{sub.year} · {sub.genre}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div>
                        <span className="text-sm text-foreground">{sub.submittedBy}</span>
                        <span className="block text-xs text-muted-foreground capitalize">{sub.submitterRole}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">{sub.submittedDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={sub.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {sub.status === 'pending' &&
                      <>
                            <button
                          onClick={() => setConfirmModal({ type: 'approve', id: sub.id, title: sub.title })}
                          className="p-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                          aria-label={`Approve ${sub.title}`}>
                          
                              <Icon name="CheckIcon" size={13} />
                            </button>
                            <button
                          onClick={() => setConfirmModal({ type: 'reject', id: sub.id, title: sub.title })}
                          className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          aria-label={`Reject ${sub.title}`}>
                          
                              <Icon name="XMarkIcon" size={13} />
                            </button>
                          </>
                      }
                        <button className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors opacity-0 group-hover:opacity-100" aria-label={`View ${sub.title} details`}>
                          <Icon name="EyeIcon" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Flagged reviews */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="w-1 h-5 bg-primary rounded-full" />
              <h2 className="text-base font-700 text-foreground">Flagged Reviews</h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-700 status-rejected">12 Flagged</span>
            </div>
            <div className="flex items-center gap-2">
              {(['all', 'high', 'medium', 'low'] as const).map((f) =>
              <button
                key={`rev-filter-${f}`}
                onClick={() => setReviewFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                reviewFilter === f ? 'bg-primary/15 text-primary border border-primary/25' : 'btn-ghost'}`
                }>
                
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {filteredReviews.map((item) =>
            <div key={item.id} className={`p-4 rounded-2xl bg-card border transition-colors group ${
            item.severity === 'high' ? 'border-primary/30 bg-primary/5' :
            item.severity === 'medium' ? 'border-gold/25 bg-gold/5' : 'border-border'}`
            }>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-700 ${
                    item.severity === 'high' ? 'bg-primary/15 text-primary border border-primary/25' :
                    item.severity === 'medium' ? 'status-pending' : 'bg-muted text-muted-foreground border border-border'}`
                    }>
                        {item.severity.toUpperCase()} SEVERITY
                      </span>
                      <span className="text-xs text-muted-foreground">
                        <strong className="text-foreground">{item.flagCount}</strong> users flagged this
                      </span>
                      <span className="text-xs text-muted-foreground">{item.date}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-600 text-foreground">{item.user}</span>
                      <span className="text-xs text-muted-foreground">on</span>
                      <span className="text-sm font-600 text-accent">{item.movie}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      <strong className="text-foreground">Reason: </strong>{item.flagReason}
                    </p>
                    <p className="text-sm text-muted-foreground italic line-clamp-2 border-l-2 border-border pl-3">
                      &ldquo;{item.excerpt}&rdquo;
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="px-3 py-1.5 rounded-lg text-xs font-600 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors flex items-center gap-1.5">
                      <Icon name="TrashIcon" size={12} />
                      Remove
                    </button>
                    <button className="px-3 py-1.5 rounded-lg text-xs font-600 btn-ghost flex items-center gap-1.5">
                      <Icon name="CheckIcon" size={12} />
                      Dismiss
                    </button>
                    <button className="px-3 py-1.5 rounded-lg text-xs font-600 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 transition-colors flex items-center gap-1.5">
                      <Icon name="UserMinusIcon" size={12} />
                      Suspend User
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User management */}
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="w-1 h-5 bg-blue-400 rounded-full" />
              <h2 className="text-base font-700 text-foreground">User Management</h2>
              <span className="text-sm text-muted-foreground">(18,430 total)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" placeholder="Search users..." className="input-dark pl-8 pr-3 py-2 text-xs w-48" />
              </div>
              <button className="btn-primary flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-600">
                <Icon name="PlusIcon" size={12} />
                Add User
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Reviews</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Watched</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden md:table-cell">Joined</th>
                  <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentUsers.map((user) =>
                <tr key={user.id} className="thread-row group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-700 text-white flex-shrink-0 ${
                      user.status === 'suspended' ? 'bg-muted' : 'bg-gradient-to-br from-primary/60 to-purple-800/60'}`
                      }>
                          {user.name.split(' ').map((n) => n[0]).join('')}
                        </div>
                        <div>
                          <p className="text-sm font-600 text-foreground">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <StatusBadge status={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground font-mono-nums">{user.reviews}</span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <span className="text-sm text-muted-foreground font-mono-nums">{user.watchedCount}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-muted-foreground">{user.joined}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors" aria-label={`View ${user.name}`}>
                          <Icon name="EyeIcon" size={13} />
                        </button>
                        <button className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors" aria-label={`Edit ${user.name}`}>
                          <Icon name="PencilIcon" size={13} />
                        </button>
                        {user.status === 'active' ?
                      <button className="p-1.5 rounded-lg text-gold hover:bg-gold/10 transition-colors" aria-label={`Suspend ${user.name}`}>
                            <Icon name="NoSymbolIcon" size={13} />
                          </button> :

                      <button className="p-1.5 rounded-lg text-green-400 hover:bg-green-500/10 transition-colors" aria-label={`Reactivate ${user.name}`}>
                            <Icon name="CheckCircleIcon" size={13} />
                          </button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-border flex items-center justify-between bg-muted/20">
              <p className="text-xs text-muted-foreground">Showing 6 of 18,430 users</p>
              <div className="flex items-center gap-2">
                <button className="btn-ghost px-3 py-1.5 rounded-lg text-xs">Previous</button>
                {[1, 2, 3].map((p) =>
                <button key={`page-${p}`} className={`w-7 h-7 rounded-lg text-xs font-600 transition-colors ${p === 1 ? 'bg-primary text-white' : 'btn-ghost'}`}>{p}</button>
                )}
                <button className="btn-ghost px-3 py-1.5 rounded-lg text-xs">Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirmModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" onClick={() => setConfirmModal(null)}>
          <div className="glass-card-elevated rounded-2xl p-6 max-w-sm w-full mx-4 shadow-modal scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${confirmModal.type === 'approve' ? 'bg-green-500/15' : 'bg-primary/15'}`}>
                <Icon
                name={confirmModal.type === 'approve' ? 'CheckCircleIcon' : 'XCircleIcon'}
                size={20}
                className={confirmModal.type === 'approve' ? 'text-green-400' : 'text-primary'} />
              
              </div>
              <h3 className="text-base font-700 text-foreground capitalize">
                {confirmModal.type} Submission
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Are you sure you want to <strong className="text-foreground">{confirmModal.type}</strong> the submission for{' '}
              <strong className="text-foreground">&ldquo;{confirmModal.title}&rdquo;</strong>?
              {confirmModal.type === 'reject' && ' This action will notify the contributor.'}
            </p>
            <div className="flex items-center gap-3">
              <button
              onClick={() => setConfirmModal(null)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-700 ${confirmModal.type === 'approve' ? 'btn-primary' : 'bg-primary/15 text-primary border border-primary/25 hover:bg-primary/25 transition-colors'}`}>
              
                Confirm {confirmModal.type.charAt(0).toUpperCase() + confirmModal.type.slice(1)}
              </button>
              <button onClick={() => setConfirmModal(null)} className="btn-ghost px-4 py-2.5 rounded-xl text-sm">Cancel</button>
            </div>
          </div>
        </div>
      }
    </div>);

}